"use client";

import React, { useState } from "react";
import { ArrowLeft, Folder, Tag } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  EcommerceProductsProvider,
  ProductPrice,
  useEcommerceProducts,
  VariableProductAddToCart,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { cn } from "@/lib/utils";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function ProductDetailConsumer() {
  const { products, loading, error, refetch } = useEcommerceProducts();
  const [activeImage, setActiveImage] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-6 space-y-4">
          <div className="aspect-square w-full animate-pulse rounded-lg bg-muted" />
          <div className="flex gap-3">
            {[0, 1, 2, 3].map((_, i) => (
              <div
                key={i}
                data-index={i}
                className="size-18 animate-pulse rounded-md bg-muted"
              />
            ))}
          </div>
        </div>
        <div className="lg:col-span-6 space-y-6">
          <div className="h-8 w-3/4 animate-pulse rounded-md bg-muted" />
          <div className="h-6 w-1/3 animate-pulse rounded-md bg-muted" />
          <div className="h-24 w-full animate-pulse rounded-md bg-muted" />
          <div className="h-12 w-full animate-pulse rounded-md bg-muted" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center text-card-foreground">
        <p className="text-base text-destructive font-medium">
          Unable to load product information.
        </p>
        <div className="mt-4">
          <Button variant="outline" size="sm" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="rounded-lg border border-border bg-card p-8 text-center text-card-foreground">
        <p className="text-base text-muted-foreground">Product not found.</p>
        <div className="mt-4">
          <Button variant="outline" size="sm" asChild>
            <Link to="/ShopPage">Browse catalog</Link>
          </Button>
        </div>
      </div>
    );
  }

  const product = products[0];
  const images = product.gallery_image_urls?.length
    ? product.gallery_image_urls
    : product.featured_image_url
    ? [product.featured_image_url]
    : [];
  const mainImage = activeImage ?? images[0];

  return (
    <article className="space-y-12">
      {/* Top Breadcrumb Navigation */}
      <nav aria-label="Product navigation" className="flex items-center gap-2">
        <Button variant="ghost" size="sm" asChild className="text-muted-foreground hover:text-foreground">
          <Link to="/ShopPage">
            <ArrowLeft className="size-4 mr-1" />
            Back to store
          </Link>
        </Button>
      </nav>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
        {/* Product Media Gallery */}
        <div className="lg:col-span-6 space-y-4">
          {mainImage ? (
            <div className="aspect-square w-full overflow-hidden rounded-lg border border-border bg-muted">
              <img
                src={mainImage}
                alt={product.title}
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          ) : (
            <div className="aspect-square w-full flex items-center justify-center rounded-lg border border-border bg-muted text-muted-foreground">
              No preview available
            </div>
          )}

          {/* Adaptive Thumbnails Strip */}
          {images.length > 1 && (
            <div
              className="flex flex-wrap gap-3"
              role="group"
              aria-label="Product gallery thumbnails"
            >
              {images.map((url, i) => {
                const isSelected = url === mainImage;
                return (
                  <Button
                    key={url + i}
                    data-index={i}
                    type="button"
                    variant="outline"
                    onClick={() => setActiveImage(url)}
                    aria-label={`${product.title} image ${i + 1}`}
                    aria-current={isSelected ? "true" : undefined}
                    className={cn(
                      "size-18 p-0 shrink-0 overflow-hidden rounded-md border bg-muted transition-all",
                      isSelected
                        ? "ring-2 ring-primary border-primary"
                        : "opacity-75 hover:opacity-100"
                    )}
                  >
                    <img
                      src={url}
                      alt=""
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        {/* Product Details & Ordering */}
        <div className="lg:col-span-6 space-y-6">
          <div className="space-y-3">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
              {product.title}
            </h1>

            <div className="flex items-baseline gap-3">
              <ProductPrice
                product={product}
                as="p"
                className="text-2xl font-bold text-foreground"
              />
            </div>
          </div>

          {/* Concise Short Description */}
          {product.short_description && (
            <div
              className="text-sm text-foreground/85 leading-relaxed [&_p]:mb-3 [&_p:last-child]:mb-0"
              dangerouslySetInnerHTML={{ __html: product.short_description }}
            />
          )}

          {/* Variations & Add to Cart */}
          <div className="rounded-lg border border-border bg-card p-5 shadow-xs space-y-4">
            <VariableProductAddToCart
              product={product}
              onVariationResolved={(variation) =>
                setActiveImage(variation?.featured_image_url ?? null)
              }
              className="w-full space-y-4"
              buttonClassName="bg-primary text-primary-foreground hover:bg-primary/90"
            />
          </div>

          {/* Metadata Section: Categories & Tags */}
          <div className="border-t border-border pt-5 space-y-3 text-xs">
            {product.categories && product.categories.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 font-medium text-muted-foreground">
                  <Folder className="size-3.5" />
                  Categories:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {product.categories.map((cat, catIndex) => (
                    <Badge
                      key={cat.id || catIndex}
                      data-index={catIndex}
                      variant="secondary"
                      asChild
                      className="font-normal"
                    >
                      <Link to={cat.link || "/ShopPage"}>{cat.name}</Link>
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {product.tags && product.tags.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1 font-medium text-muted-foreground">
                  <Tag className="size-3.5" />
                  Tags:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {product.tags.map((tg, tagIndex) => (
                    <Badge
                      key={tg.id || tagIndex}
                      data-index={tagIndex}
                      variant="outline"
                      asChild
                      className="font-normal"
                    >
                      <Link to={tg.link || "/ShopPage"}>{tg.name}</Link>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Expandable Product Information */}
      {product.long_description && (
        <div className="border-t border-border pt-8">
          <Accordion type="single" collapsible defaultValue="full-description" className="w-full">
            <AccordionItem value="full-description" className="border-border">
              <AccordionTrigger className="text-base font-semibold text-foreground">
                Product description & specifications
              </AccordionTrigger>
              <AccordionContent className="text-sm text-foreground/85 leading-relaxed pt-3">
                <div
                  className="prose max-w-none text-foreground [&_p]:mb-4 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-1"
                  dangerouslySetInnerHTML={{ __html: product.long_description }}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      )}
    </article>
  );
}

export default function SingleProduct() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  const wpQuery = new WP_Query(currentWPQueryParams);

  return (
    <section
      id="single-product-template-section"
      data-nav="light"
      className="bg-background text-foreground py-12 md:py-16"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <EcommerceProductsProvider wp_query={wpQuery}>
          <ProductDetailConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}